---
title: "Match rating for candidates"
description: "How the AI match score works in Chosen: it derives testable claims from the job post, scores each against the resume with cited evidence, and gives an auditable 0-100% rating."
category: "candidates"
keywords:
  - "match rating"
  - "AI candidate score"
  - "candidate match score"
  - "resume job match"
schema: "HowTo"
nav:
  section: "guides"
  order: 50
  parent: "/guides/candidates"
---

The match rating is a 0–100% score shown on each candidate for a given job. It estimates how well their resume fits that role. It's built to be auditable — you can see exactly which requirements drove the number — and it's a way to sort a pile, not a verdict on a person.

## How the score is built

Chosen doesn't eyeball a resume and pick a number. It works in steps:

<Steps>
  <li>It reads the job posting and derives a set of concrete, testable claims about what the role needs — usually 5 to 15 of them ("3+ years of backend experience", "has shipped a payments integration").</li>
  <li>It scores each claim against the candidate's resume, and cites a specific phrase from the resume as evidence for the score.</li>
  <li>It combines the per-claim scores, weighted by how much each requirement matters, into the final percentage.</li>
</Steps>

Because every claim carries its evidence, the score is inspectable. A 72% isn't a vibe — it's a list of requirements, each marked met or not, each pointing at the line of the resume that decided it. When a number looks wrong, you can find out why.

## What a good description gets you

The score is only ever as good as the job post it reads. Claims come straight from your description, so a vague description produces vague claims and a soft, uninformative score.

If you want sharper ratings, write a sharper job post — specific requirements, the things that actually matter for this role. The same description feeds the [careers page](/guides/careers) candidates read, so this is effort that pays off twice.

<Callout type="warning" title="Editing the job description recomputes scores — and stales your saved searches">
Match scores are derived from the job description. Edit that description and Chosen rebuilds the claims and recomputes every candidate's score for the job. That's correct behavior. But a [saved search](/guides/search/saved-searches) is a snapshot taken when you ran it — it won't reflect the new scores until you re-run it. After a meaningful description edit, re-run any saved search that filters or sorts on match rating.
</Callout>

## It's a sorting aid, not a hiring decision

Treat the score as a way to order a long list, not a ruling on who's good.

A 45% can be exactly the right hire. It usually means the job post didn't capture what you actually want — the candidate's real strengths weren't in the description, so no claim rewarded them. The fix is to read the candidate, not to trust the percentage. A high score is a reason to look sooner; a low score is a reason to look, not a reason to skip.

The score is honest about one thing in particular: a requirement the resume simply doesn't mention counts as a gap, not a pass. So a strong candidate with a thin resume can score lower than a weaker one who wrote more. That's a reason to read resumes, which you were going to do anyway.

## Limits

Match rating compares a resume to a job post. It doesn't know your team, the hiring manager's actual bar, or anything said in an interview. It depends on a clean parse — see [resume parsing](/guides/candidates/resume-parsing) — and on a job description worth reading. It's a fast first pass over a stack of candidates. The hiring decision is still yours.
